// ==========================================
// OD_TOPBAR.JS - Minimal Flutter-inspired
// Unified topbar with logo support
// ==========================================
import { getLang, setLang, t } from './od_i18n.js';

const _svgEN = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="19" viewBox="0 0 22 15"><rect width="22" height="15" fill="#fff"/><rect y="0" width="22" height="1.154" fill="#B22234"/><rect y="2.308" width="22" height="1.154" fill="#B22234"/><rect y="4.615" width="22" height="1.154" fill="#B22234"/><rect y="6.923" width="22" height="1.154" fill="#B22234"/><rect y="9.231" width="22" height="1.154" fill="#B22234"/><rect y="11.538" width="22" height="1.154" fill="#B22234"/><rect y="13.846" width="22" height="1.154" fill="#B22234"/><rect width="9" height="8.077" fill="#3C3B6E"/></svg>`;
const _svgES = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="19" viewBox="0 0 22 15"><rect width="22" height="15" fill="#AA151B"/><rect y="3.75" width="22" height="7.5" fill="#F1BF00"/></svg>`;

/**
 * Inject topbar HTML once into the DOM
 * @param {Object} config - Configuration options
 * @param {string} [config.logoSrc] - Path to logo image (default: './favicon.png')
 * @param {boolean} [config.showLogo] - Show logo (default: true)
 */
function injectTopbarOnce(config = {}) {
  if (document.getElementById("odTopbar")) return;

  if (!document.getElementById("odLangStyles")) {
    const style = document.createElement("style");
    style.id = "odLangStyles";
    style.textContent = [
      ".odLangSelector { position: relative; display: flex; align-items: center; }",
      /* strip the default button chrome for both flag buttons */
      "#odTopbar .odLangTrigger, #odTopbar .odLangOption { border: none !important; box-shadow: none !important; background: transparent !important; }",
      ".odLangTrigger { padding: 4px 8px !important; }",
      /* override the global .odTopbarBtn svg rule (14×14) so the flag is tall enough */
      ".odLangTrigger svg, .odLangOption svg { width: auto !important; height: 21px !important; display: block !important; border-radius: 2px; }",
      /* dropdown: warm-white theme matching topbar, fade via opacity+visibility */
      ".odLangDropdown { position: absolute; top: calc(100% + 6px); right: 0; background: rgba(245,242,237,0.97); border: 1px solid #e2ddd6; border-radius: 6px; padding: 4px; z-index: 9999; box-shadow: 0 4px 14px rgba(0,0,0,0.09); opacity: 0; visibility: hidden; pointer-events: none; transition: opacity 0.15s ease, visibility 0.15s; backdrop-filter: blur(8px); }",
      ".odLangDropdown.odOpen { opacity: 1; visibility: visible; pointer-events: auto; }",
      ".odLangOption { padding: 5px 7px !important; border-radius: 4px !important; }",
      ".odLangOption:hover { background: rgba(0,0,0,0.05) !important; }",
    ].join("\n");
    document.head.appendChild(style);
  }

  const logoSrc = config.logoSrc || './favicon.png';
  const showLogo = config.showLogo !== false;

  const bar = document.createElement("div");
  bar.id = "odTopbar";
  bar.className = "odTopbar"; // hidden by CSS until odTopbarShow
  
  bar.innerHTML = `
    <div class="odTopbarLeft">
      ${showLogo ? `<img src="${logoSrc}" class="odTopbarLogo" alt="OpenDraw" />` : ''}
      
      <a id="odBtnGoOrganizer" class="odTopbarBtn" href="./organizer.html">
        <span>Organizer</span>
      </a>
      
      <a id="odBtnGoEditor" class="odTopbarBtn" href="./editor.html">
        <span>Editor</span>
      </a>

      <div class="odTopbarWho" title="Signed in">
        <span class="odTopbarDot"></span>
        <span id="odWhoName" class="odTopbarWhoName">user</span>
      </div>
    </div>

    <div class="odTopbarRight">
      <div class="odLangSelector" id="odLangSelector">
        <button class="odLangTrigger odTopbarBtn" id="odLangTrigger" title="Change language / Cambiar idioma">
          ${getLang() === 'es' ? _svgES : _svgEN}
        </button>
        <div class="odLangDropdown" id="odLangDropdown">
          <button class="odLangOption odTopbarBtn" data-lang="${getLang() === 'es' ? 'en' : 'es'}" title="${getLang() === 'es' ? 'English' : 'Español'}">
            ${getLang() === 'es' ? _svgEN : _svgES}
          </button>
        </div>
      </div>
      <div class="odTopbarSep"></div>
      <button id="odBtnHelp" class="odTopbarBtn" type="button" style="display:none">
        <span>${t('topbar_help_btn')}</span>
      </button>
      <div class="odTopbarSep" id="odHelpSep" style="display:none"></div>
      <button id="odBtnLogoutTop" class="odTopbarBtn odTopbarBtnPrimary" type="button">
        <span>${t('topbar_logout')}</span>
      </button>
    </div>
  `;

  document.body.insertBefore(bar, document.body.firstChild);
}

/**
 * Format user display name
 * @param {Object} user - User object from auth
 * @returns {string} Formatted display name
 */
function formatWho(user) {

  if (!user) return "User";

  // ---- helper: Title Case ----
  function titleCaseName(s){
    return String(s || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(w =>
        w.charAt(0).toUpperCase() +
        w.slice(1).toLowerCase()
      )
      .join(" ");
  }

  // 1️⃣ displayName (best source)
  if (user.displayName && user.displayName.trim()) {
    return titleCaseName(user.displayName);
  }

  // 2️⃣ email fallback
  if (user.email) {

    // before @
    let emailName = user.email.split("@")[0];

    // juan.perez / juan_perez / juan-perez
    emailName = emailName
      .replace(/[._-]+/g," ")
      .replace(/\d+/g," "); // remove numbers if any

    const clean = titleCaseName(emailName);

    if (clean) return clean;
  }

  // 3️⃣ uid fallback
  if (user.uid) {
    return `User ${user.uid.substring(0,6)}`;
  }

  return "User";
}
/**
 * Add loading state to topbar
 * @param {boolean} isLoading - Loading state
 */
function setTopbarLoading(isLoading) {
  const bar = document.getElementById("odTopbar");
  if (!bar) return;
  
  if (isLoading) {
    bar.classList.add("odTopbarLoading");
  } else {
    bar.classList.remove("odTopbarLoading");
  }
}

/**
 * Initialize OpenDraw topbar
 * @param {Object} opts - Configuration options
 * @param {Function} opts.subscribeAuth - Auth subscription function (cb) => unsubscribeFn | null
 * @param {Function} opts.logout - Logout function () => Promise<void>
 * @param {Function} [opts.onNoUser] - Callback when no user is detected () => void
 * @param {Object} [opts.links] - Custom links { organizerHref, editorHref, homeHref }
 * @param {boolean} [opts.redirectOnLogout] - Redirect to home after logout (default: true)
 * @param {string} [opts.logoSrc] - Path to logo image (default: './favicon.png')
 * @param {boolean} [opts.showLogo] - Show logo (default: true)
 * @returns {Function|null} Unsubscribe function or null
 */
export function initTopbar(opts) {
  // Validation
  if (!opts || typeof opts.subscribeAuth !== "function" || typeof opts.logout !== "function") {
    throw new Error("initTopbar requires subscribeAuth(cb) and logout() functions");
  }

  // Inject topbar into DOM
  injectTopbarOnce({
    logoSrc: opts.logoSrc,
    showLogo: opts.showLogo
  });

  // Get DOM elements
  const bar     = document.getElementById("odTopbar");
  const who     = document.getElementById("odWhoName");
  const btnOut  = document.getElementById("odBtnLogoutTop");
  const btnOrg  = document.getElementById("odBtnGoOrganizer");
  const btnEd   = document.getElementById("odBtnGoEditor");
  const btnHelp = document.getElementById("odBtnHelp");
  const helpSep = document.getElementById("odHelpSep");

  if (!bar) {
    return null;
  }

  // Configure custom links
  if (opts.links?.organizerHref && btnOrg) {
    btnOrg.href = opts.links.organizerHref;
  }
  if (opts.links?.editorHref && btnEd) {
    btnEd.href = opts.links.editorHref;
  }

  // ---- Context-aware buttons (robust) ----
  const body = document.body;
  const path = (window.location.pathname || "").toLowerCase();
  const href = (window.location.href || "").toLowerCase();

  // Prefer explicit body class if present (Organizer already has organizerPage)
  const isOrganizerPage =
    body?.classList?.contains("organizerPage") ||
    path.includes("/organizer") ||
    path.includes("organizer.html") ||
    href.includes("organizer");

  const isEditorPage =
    body?.classList?.contains("editorPage") || // si lo agregás en editor.html, queda 100% perfecto
    path.includes("/editor") ||
    path.includes("editor.html") ||
    href.includes("editor");

function hideBtn(btn) {
  if (!btn) return;

  // Blindaje contra CSS con !important
  btn.style.setProperty("display", "none", "important");

  // Accesibilidad / tab
  btn.setAttribute("aria-hidden", "true");
  btn.tabIndex = -1;
}
  function makePrimary(btn) {
    if (!btn) return;
    btn.classList.add("odTopbarBtnPrimary");
  }

  // If both true for any weird reason, pick organizer first
  if (isOrganizerPage) {
    hideBtn(btnOrg);
    makePrimary(btnEd);
  } else if (isEditorPage) {
    hideBtn(btnEd);
    makePrimary(btnOrg);
  }

  // Wire Editor button to last play (only once) - only if visible
  if (btnEd && !btnEd.hidden && !btnEd.dataset.wiredLast) {
    btnEd.dataset.wiredLast = "1";

    btnEd.addEventListener("click", (e) => {
      const editorHref = btnEd.getAttribute("href") || "./editor.html";
      const lastHref = getLastEditorHref(editorHref);

      if (lastHref) {
        e.preventDefault();
        window.location.href = lastHref;
        return;
      }

      // No last playId saved yet -> go Organizer (choose a play)
      e.preventDefault();
      window.location.href = "./organizer.html";
    });
  }

  const homeHref = opts.links?.homeHref || "./index.html";
  const redirectOnLogout = opts.redirectOnLogout !== false;

  // Wire language selector dropdown
  const langTrigger = document.getElementById('odLangTrigger');
  const langDropdown = document.getElementById('odLangDropdown');
  if (langTrigger && !langTrigger.dataset.wired) {
    langTrigger.dataset.wired = "1";
    langTrigger.addEventListener('click', e => {
      e.stopPropagation();
      langDropdown.classList.toggle('odOpen');
    });
    document.addEventListener('click', () => langDropdown.classList.remove('odOpen'));
  }
  document.querySelectorAll('.odLangOption').forEach(btn => {
    if (!btn.dataset.wired) {
      btn.dataset.wired = "1";
      btn.addEventListener('click', () => setLang(btn.dataset.lang));
    }
  });

  // Wire ¿Cómo funciona? button
  if (btnHelp && typeof opts.onHelp === "function") {
    btnHelp.style.display = "";
    if (helpSep) helpSep.style.display = "";
    if (!btnHelp.dataset.wired) {
      btnHelp.dataset.wired = "1";
      btnHelp.addEventListener("click", () => opts.onHelp());
    }
  }

  // Wire logout button (only once)
  if (btnOut && !btnOut.dataset.wired) {
    btnOut.dataset.wired = "1";

    btnOut.addEventListener("click", async () => {
      try {
        setTopbarLoading(true);
        btnOut.disabled = true;
        btnOut.querySelector("span").textContent = t('topbar_logging_out');

        await opts.logout();

      } catch (error) {
        btnOut.querySelector("span").textContent = t('topbar_logout');

      } finally {
        setTopbarLoading(false);
        btnOut.disabled = false;

        if (redirectOnLogout) {
          window.location.href = homeHref;
        }
      }
    });
  }

  // Subscribe to auth state changes
  const unsub = opts.subscribeAuth((user) => {
    if (!bar) return;

    if (!user) {
      // No user logged in
      bar.classList.remove("odTopbarShow");
      document.body.classList.remove("odHasTopbar");

      if (typeof opts.onNoUser === "function") {
        opts.onNoUser();
      }

      return;
    }

    // User is logged in
    bar.classList.add("odTopbarShow");
    document.body.classList.add("odHasTopbar");

    if (who) {
      who.textContent = formatWho(user);
      who.title = user.email || "Signed in";
    }
  });

  return unsub || null;
}
/**
 * Manually show/hide topbar
 * @param {boolean} show - Show or hide topbar
 */
export function toggleTopbar(show) {
  const bar = document.getElementById("odTopbar");
  if (!bar) return;
  
  if (show) {
    bar.classList.add("odTopbarShow");
    document.body.classList.add("odHasTopbar");
  } else {
    bar.classList.remove("odTopbarShow");
    document.body.classList.remove("odHasTopbar");
  }
}
function getLastEditorHref(fallbackEditorHref = "./editor.html") {
  let playId = "";
  try { playId = (localStorage.getItem("od_editor_playId") || "").trim(); } catch {}
  if (playId) return `${fallbackEditorHref}?playId=${encodeURIComponent(playId)}`;
  return ""; // señal de "no tengo"
}

/**
 * Update user display name manually
 * @param {string} name - New display name
 */
export function updateTopbarUser(name) {
  const who = document.getElementById("odWhoName");
  if (who && name) {
    who.textContent = name;
  }
}

/**
 * Update topbar button href
 * @param {string} buttonId - Button ID ('odBtnGoOrganizer' or 'odBtnGoEditor')
 * @param {string} href - New href value
 */
export function updateTopbarLink(buttonId, href) {
  const btn = document.getElementById(buttonId);
  if (btn && href) {
    btn.href = href;
  }
}

// Export loading state control
export { setTopbarLoading };